import type { Signal, SignalKey } from "./types";

/** Нормализованный снимок погоды. Собирается в api/weather.ts. */
export interface Observation {
  temp: number;
  apparent: number;
  isDay: boolean;
  code: number;
  cloud: number;
  precip: number;
  wind: number;
  gusts: number;
  /** Откуда дует, градусы (метеорологические: 0 — с севера, 90 — с востока) */
  windDir?: number;
  pressure: number;
  /** Локальное время наблюдения, ISO без зоны */
  now: string;
  /** past_days + сегодня; последний элемент — сегодня */
  daily: { time: string[]; code: number[]; tmean: (number | null)[] };
  sunset?: string;
  /** Давление за последние часы, по возрастанию времени */
  pressureTrend: number[];
  /** Вероятность осадков на ближайшие часы, % */
  precipSoon: number[];
}

const WMO = {
  isWet: (c: number) => (c >= 51 && c <= 67) || (c >= 80 && c <= 82) || c >= 95,
  isSnow: (c: number) => (c >= 71 && c <= 77) || c === 85 || c === 86,
  isFog: (c: number) => c === 45 || c === 48,
  isGrey: (c: number) => c >= 3,
};

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Изменение ряда по наклону наименьших квадратов, приведённое ко всей длине
 * окна. Устойчиво к одиночному выбросу, в отличие от разницы концов.
 */
function trend(ys: number[]): number {
  const n = ys.length;
  if (n < 2) return 0;
  const mx = (n - 1) / 2;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0;
  let sxx = 0;
  for (let i = 0; i < n; i++) {
    sxy += (i - mx) * (ys[i]! - my);
    sxx += (i - mx) ** 2;
  }
  return sxx === 0 ? 0 : (sxy / sxx) * (n - 1);
}
const one = (d: number) => (Math.round(d * 10) / 10).toFixed(1).replace(".0", "");

/**
 * Погода → сигналы. Чистая функция, без сети.
 * Порядок значения не имеет: приоритеты живут в profile.ts.
 */
export function toSignals(o: Observation): Signal[] {
  const out: Signal[] = [];
  const add = (key: SignalKey, intensity: number, confidence: number, evKey: string, evValue = "") => {
    if (intensity > 0) out.push({ key, intensity: clamp01(intensity), confidence, evKey, evValue });
  };

  // --- серия серых дней: то, что человек реально замечает ---
  const last = o.daily.time.length - 1;
  let streak = 0;
  for (let i = last; i >= 0; i--) {
    const c = o.daily.code[i];
    if (c !== undefined && WMO.isGrey(c)) streak++;
    else break;
  }
  if (streak >= 3) add("grey_streak", clamp01(streak / 7), 1, "grey_streak", String(streak));

  // --- дельта к вчера ---
  const tToday = o.daily.tmean[last];
  const tYest = o.daily.tmean[last - 1];
  if (tToday != null && tYest != null) {
    const d = tToday - tYest;
    if (d <= -2) add("temp_drop", clamp01(-d / 8), 1, "temp_drop", one(-d));
    if (d >= 2) add("temp_rise", clamp01(d / 8), 1, "temp_rise", one(d));
  }

  // --- аномалия к недельной базе (не климатическая норма — базы за 30 лет тут нет) ---
  //
  // Сравниваются СУТОЧНЫЕ СРЕДНИЕ с суточными средними. Раньше здесь стояла
  // мгновенная o.temp против среднего суточных: обычный ход дня сам по себе
  // даёт больше полутора сигм, поэтому «аномально тепло» объявлялось почти
  // каждый ясный день после обеда, а «аномально холодно» — каждую ясную ночь.
  const base = o.daily.tmean.slice(0, last).filter((v): v is number => v != null);
  const todayMean = tToday ?? o.temp;
  if (base.length >= 5) {
    const mean = base.reduce((a, b) => a + b, 0) / base.length;
    const sd = Math.sqrt(base.reduce((a, b) => a + (b - mean) ** 2, 0) / base.length) || 1;
    const z = (todayMean - mean) / sd;
    if (z >= 1.5) add("anomaly_warm", 0.4 + 0.6 * clamp01((z - 1.5) / 2), 0.7, "anomaly_warm");
    if (z <= -1.5) add("anomaly_cold", 0.4 + 0.6 * clamp01((-z - 1.5) / 2), 0.7, "anomaly_cold");
  }

  // --- температурная полоса ---
  //
  // Полосу выбирает ОЩУЩАЕМАЯ температура: +8 при сильном ветре человек
  // проживает как +1 и одевается по второму. Число во фразе остаётся
  // термометровым — оно же стоит крупно в шапке.
  //
  // У каждой полосы есть пол интенсивности. Без него ровно на границе
  // (25.0°, 6.0°) выходил ноль, add() такой сигнал пропускал, а else-if не
  // давал сработать соседней полосе — температуры в профиле не было вовсе.
  const t = o.temp;
  const feel = Number.isFinite(o.apparent) ? o.apparent : t;
  if (feel >= 25) add("hot", 0.5 + 0.5 * clamp01((feel - 25) / 12), 1, "hot", one(t));
  else if (feel >= 16) add("warm", 1, 1, "warm", one(t));
  else if (feel >= 6) add("cool", 1, 1, "cool", one(t));
  else if (feel >= -8) add("cold", 0.5 + 0.5 * clamp01((6 - feel) / 14), 1, "cold", one(t));
  else add("frost", 0.6 + 0.4 * clamp01((-8 - feel) / 15), 1, "frost", one(t));

  // --- небо ---
  if (o.cloud <= 20) add("clear", clamp01((20 - o.cloud) / 20 + 0.3), 1, o.isDay ? "clear_day" : "clear_night");
  if (o.cloud >= 80) add("overcast", clamp01((o.cloud - 80) / 20 + 0.4), 1, "overcast");

  // --- осадки ---
  if (WMO.isSnow(o.code)) add("snow", clamp01(o.precip / 3 + 0.5), 1, "snow");
  else if (o.precip >= 2.5) add("heavy_rain", clamp01(o.precip / 8), 1, "heavy_rain", one(o.precip));
  else if (WMO.isWet(o.code) || o.precip > 0) add("rain", clamp01(o.precip / 3 + 0.45), 1, "rain", o.precip > 0 ? one(o.precip) : "");
  if (WMO.isFog(o.code)) add("fog", 0.9, 1, "fog");

  // --- ветер ---
  const w = Math.max(o.wind, o.gusts * 0.8);
  if (w >= 50) add("storm", clamp01((w - 50) / 30 + 0.4), 1, "storm", String(Math.round(w)));
  // Ровно на пороге получался ноль: сигнала не было при 20 км/ч и он весил
  // сотые доли при 21, но уже занимал место в веере запросов.
  else if (w >= 20) add("windy", 0.4 + 0.6 * clamp01((w - 20) / 30), 1, "windy", String(Math.round(w)));

  // --- свет ---
  if (!o.isDay) add("night", 1, 1, "night");
  if (o.sunset && o.isDay) {
    const min = (Date.parse(o.sunset) - Date.parse(o.now)) / 60000;
    if (min > 0 && min <= 60) add("golden_hour", clamp01((60 - min) / 60 + 0.4), 1, "golden_hour", String(Math.round(min)));
  }

  // --- давление: фронт ---
  //
  // По наклону всего ряда, а не по двум концам: один скачок в последнем
  // отсчёте прятал падение, шедшее все предыдущие часы.
  if (o.pressureTrend.length >= 4) {
    const d = trend(o.pressureTrend);
    if (d <= -1.5) add("pressure_falling", clamp01(-d / 6), 0.9, "pressure_falling", one(-d));
    if (d >= 1.5) add("pressure_rising", clamp01(d / 6), 0.9, "pressure_rising", one(d));
  }

  // --- ход дня: прогнозные, поэтому confidence ниже ---
  if (o.precipSoon.length >= 3) {
    const soon = Math.max(...o.precipSoon.slice(0, 3));
    const wetNow = WMO.isWet(o.code) || o.precip > 0;
    if (wetNow && soon <= 20) add("clearing", 0.7, 0.6, "clearing");
    if (!wetNow && soon >= 60) add("worsening", clamp01(soon / 100), 0.6, "worsening", String(soon));
  }

  return out;
}

/** Порядок, в котором сигналы попадают во фразу о дне. */
export const SENTENCE_ORDER: SignalKey[] = [
  "grey_streak", "clear", "overcast", "snow", "heavy_rain", "rain", "fog",
  "storm", "windy", "temp_drop", "temp_rise", "anomaly_warm", "anomaly_cold",
  "golden_hour", "pressure_falling", "clearing", "worsening",
];

export const BAND_KEYS: SignalKey[] = ["hot", "warm", "cool", "cold", "frost"];
